/**
 * Admin Anomaly Management Service
 * Frontend service for admin-level anomaly detection management
 * Implements Requirements: 2.1, 2.2, 2.4, 2.5
 */

import { apiClient } from './api.client';

export interface PolicyConfig {
  burst: {
    product_scans_per_hour: number;
    product_scans_per_day: number;
    ip_scans_per_minute: number;
    distinct_products_per_ip_hour: number;
    ignore_repeat_seconds: number;
  };
  geo: {
    enabled: boolean;
    country_jump_minutes: number;
  };
  network: {
    datacenter_weight: number;
    vpn_weight: number;
  };
  weights: {
    BURST: number;
    SCRAPING: number;
    GEO_JUMP: number;
    DATACENTER: number;
    VPN: number;
    POST_CONSUMED: number;
  };
  decision: {
    medium: number;
    high: number;
    high_requires_multiple_signals: boolean;
  };
}

export interface AnomalyPolicy {
  scope: 'global' | 'manufacturer' | 'product';
  scopeKey: string;
  config: PolicyConfig;
  isActive: boolean;
  updatedAt: Date;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface AnomalyFlag {
  id: string;
  productId: string;
  qrToken: string;
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  reasons: string[];
  firstSeen: Date;
  lastSeen: Date;
  status: 'open' | 'acknowledged' | 'dismissed';
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  details?: any;
  createdAt: Date;
  updatedAt: Date;
  product?: any;
  acknowledger?: any;
}

export interface AnomalyFlagStats {
  totalFlags: number;
  openFlags: number;
  acknowledgedFlags: number;
  dismissedFlags: number;
  averageRiskScore: number;
  highRiskFlags: number;
  mediumRiskFlags: number;
  lowRiskFlags: number;
}

export interface AnomalyAnalytics extends AnomalyFlagStats {
  flagResolutionRate: number;
  averageResponseTime: number;
  recentActivity: Array<{
    id: string;
    productId: string;
    riskLevel: string;
    riskScore: number;
    status: string;
    createdAt: Date;
    reasons: string[];
  }>;
}

export interface AnomalyFlagFilters {
  status?: 'open' | 'acknowledged' | 'dismissed';
  riskLevel?: 'low' | 'medium' | 'high';
  manufacturerId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  limit?: number;
  offset?: number;
}

class AdminAnomalyService {
  /**
   * Get anomaly policies
   */
  async getPolicies(scope?: string, scopeKey?: string): Promise<{ global?: AnomalyPolicy }> {
    const params = new URLSearchParams();
    if (scope) params.append('scope', scope);
    if (scopeKey) params.append('scopeKey', scopeKey);

    const response = await apiClient.get(`/admin/anomaly/policies?${params.toString()}`);
    const data = response.data?.data;
    if (data && (data as AnomalyPolicy).scope && (data as AnomalyPolicy).config) {
      return { global: data as AnomalyPolicy };
    }
    return data || {};
  }

  /**
   * Update anomaly policy
   */
  async updatePolicy(scope: string, scopeKey: string, config: PolicyConfig): Promise<void> {
    await apiClient.put(`/admin/anomaly/policies/${scope}/${scopeKey}`, { config });
  }

  /**
   * Validate policy configuration
   */
  async validatePolicy(config: PolicyConfig): Promise<ValidationResult> {
    const response = await apiClient.post('/admin/anomaly/policies/validate', { config });
    return response.data.data;
  }

  /**
   * Get anomaly flags for admin dashboard
   */
  async getFlags(filters: AnomalyFlagFilters = {}): Promise<{
    flags: AnomalyFlag[];
    total: number;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.riskLevel) params.append('riskLevel', filters.riskLevel);
    if (filters.manufacturerId) params.append('manufacturerId', filters.manufacturerId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get(`/admin/anomaly/flags?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get anomaly flag statistics
   */
  async getFlagStats(filters: Pick<AnomalyFlagFilters, 'manufacturerId' | 'dateFrom' | 'dateTo'> = {}): Promise<AnomalyFlagStats> {
    const params = new URLSearchParams();
    
    if (filters.manufacturerId) params.append('manufacturerId', filters.manufacturerId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());

    const response = await apiClient.get(`/admin/anomaly/flags/stats?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Update flag status
   */
  async updateFlagStatus(flagId: string, status: 'acknowledged' | 'dismissed', notes?: string): Promise<AnomalyFlag> {
    const response = await apiClient.put(`/admin/anomaly/flags/${flagId}/status`, {
      status,
      notes
    });
    return response.data.data;
  }

  /**
   * Reopen a flag
   */
  async reopenFlag(flagId: string, reason?: string): Promise<AnomalyFlag> {
    const response = await apiClient.put(`/admin/anomaly/flags/${flagId}/reopen`, {
      reason
    });
    return response.data.data;
  }

  /**
   * Cleanup old flags
   */
  async cleanupFlags(days: number = 90): Promise<{ deletedCount: number }> {
    const response = await apiClient.delete(`/admin/anomaly/flags/cleanup?days=${days}`);
    return response.data.data;
  }

  /**
   * Get anomaly analytics
   */
  async getAnalytics(filters: Pick<AnomalyFlagFilters, 'manufacturerId' | 'dateFrom' | 'dateTo'> = {}): Promise<AnomalyAnalytics> {
    const params = new URLSearchParams();
    
    if (filters.manufacturerId) params.append('manufacturerId', filters.manufacturerId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());

    const response = await apiClient.get(`/admin/anomaly/analytics?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get default policy configuration for form initialization
   */
  getDefaultPolicyConfig(): PolicyConfig {
    return {
      burst: {
        product_scans_per_hour: 20,
        product_scans_per_day: 60,
        ip_scans_per_minute: 10,
        distinct_products_per_ip_hour: 25,
        ignore_repeat_seconds: 60
      },
      geo: {
        enabled: true,
        country_jump_minutes: 120
      },
      network: {
        datacenter_weight: 20,
        vpn_weight: 10
      },
      weights: {
        BURST: 40,
        SCRAPING: 30,
        GEO_JUMP: 35,
        DATACENTER: 20,
        VPN: 10,
        POST_CONSUMED: 25
      },
      decision: {
        medium: 40,
        high: 70,
        high_requires_multiple_signals: true
      }
    };
  }

  /**
   * Format risk level for display
   */
  formatRiskLevel(level: string): string {
    switch (level) {
      case 'low': return 'Low Risk';
      case 'medium': return 'Medium Risk';
      case 'high': return 'High Risk';
      default: return level;
    }
  }

  /**
   * Get risk level color class
   */
  getRiskLevelColor(level: string): string {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Format flag status for display
   */
  formatFlagStatus(status: string): string {
    switch (status) {
      case 'open': return 'Open';
      case 'acknowledged': return 'Acknowledged';
      case 'dismissed': return 'Dismissed';
      default: return status;
    }
  }

  /**
   * Get flag status color class
   */
  getFlagStatusColor(status: string): string {
    switch (status) {
      case 'open': return 'text-red-600 bg-red-100';
      case 'acknowledged': return 'text-blue-600 bg-blue-100';
      case 'dismissed': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  }
}

export const adminAnomalyService = new AdminAnomalyService();
